'use client';

import * as React from 'react';
import { useFirestore, useAuth } from '@/firebase';
import { MediaItem } from '@/models/media-item';
import { HybridMediaItem } from '@/lib/gallery-hybrid';
import { moveMediaItem, copyMediaItem } from '@/lib/gallery-actions';
import { getHierarchyCache, HierarchyNode } from '@/lib/gallery-cache';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Folder, FolderOpen, ChevronRight, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';

type Mode = 'move' | 'copy';

interface MoveCopyMediaDialogProps {
    open: boolean;
    onClose: () => void;
    media: MediaItem | HybridMediaItem;
    mode: Mode;
    onComplete: () => void;
}

export function MoveCopyMediaDialog({
    open,
    onClose,
    media,
    mode,
    onComplete
}: MoveCopyMediaDialogProps) {
    const firestore = useFirestore();
    const auth = useAuth();
    const { toast } = useToast();

    const [hierarchy, setHierarchy] = React.useState<HierarchyNode[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [selectedAlbum, setSelectedAlbum] = React.useState<{ id: string; name: string; path: string } | null>(null);

    // Load album hierarchy when dialog opens
    React.useEffect(() => {
        if (!open || !firestore) return;

        async function loadHierarchy() {
            setLoading(true);
            try {
                const role = auth.currentUser ? 'members' : 'public';
                let cache = await getHierarchyCache(firestore!, role);

                // Fallback to public if members cache fails
                if (!cache && role === 'members') {
                    cache = await getHierarchyCache(firestore!, 'public');
                }

                setHierarchy(cache || []);
            } catch (error) {
                console.error('Error loading hierarchy:', error);
                toast({
                    variant: 'destructive',
                    title: 'Error',
                    description: 'Failed to load album list'
                });
            } finally {
                setLoading(false);
            }
        }

        loadHierarchy();
        setSelectedAlbum(null);
    }, [open, firestore, auth.currentUser]);

    const handleConfirm = async () => {
        if (!firestore || !selectedAlbum) return;

        // Don't allow moving/copying to the same album
        if (selectedAlbum.id === media.albumId) {
            toast({
                variant: 'destructive',
                title: 'Invalid Selection',
                description: 'Please select a different album'
            });
            return;
        }

        setSaving(true);
        try {
            if (mode === 'move') {
                await moveMediaItem(firestore, media.id, selectedAlbum.id, selectedAlbum.path);
                toast({
                    title: 'Media Moved',
                    description: `Moved to "${selectedAlbum.name}"`
                });
            } else {
                await copyMediaItem(firestore, media.id, selectedAlbum.id, selectedAlbum.path);
                toast({
                    title: 'Media Copied',
                    description: `Copied to "${selectedAlbum.name}"`
                });
            }

            onComplete();
            onClose();
        } catch (error) {
            console.error(`Error ${mode}ing media:`, error);
            toast({
                variant: 'destructive',
                title: 'Error',
                description: `Failed to ${mode} media`
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{mode === 'move' ? 'Move' : 'Copy'} Media</DialogTitle>
                    <DialogDescription>
                        Select the album to {mode} this item to.
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : hierarchy.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            No albums found
                        </div>
                    ) : (
                        <ScrollArea className="h-[300px] border rounded-md p-2">
                            {hierarchy.map((node) => (
                                <AlbumTreeItem
                                    key={node.id}
                                    node={node}
                                    depth={0}
                                    selectedId={selectedAlbum?.id}
                                    currentAlbumId={media.albumId}
                                    onSelect={(id, name, path) => setSelectedAlbum({ id, name, path })}
                                />
                            ))}
                        </ScrollArea>
                    )}

                    {selectedAlbum && (
                        <div className="mt-3 p-2 bg-muted rounded-md text-sm">
                            <span className="text-muted-foreground">Selected: </span>
                            <span className="font-medium">{selectedAlbum.name}</span>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm} disabled={saving || !selectedAlbum}>
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {mode === 'move' ? 'Move' : 'Copy'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

interface AlbumTreeItemProps {
    node: HierarchyNode;
    depth: number;
    selectedId?: string;
    currentAlbumId: string;
    onSelect: (id: string, name: string, path: string) => void;
}

function AlbumTreeItem({ node, depth, selectedId, currentAlbumId, onSelect }: AlbumTreeItemProps) {
    const [expanded, setExpanded] = React.useState(depth < 1);
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = node.id === selectedId;
    const isCurrent = node.id === currentAlbumId;

    // Build path from node hierarchy (simplified - uses node name)
    const buildPath = (n: HierarchyNode, parentPath: string = ''): string => {
        const slug = n.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        return parentPath ? `${parentPath}/${slug}` : slug;
    };

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        setExpanded(!expanded);
    };

    const handleSelect = () => {
        if (!isCurrent) {
            onSelect(node.id, node.name, buildPath(node));
        }
    };

    return (
        <div>
            <div
                className={cn(
                    'flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer transition-colors',
                    isSelected && 'bg-primary text-primary-foreground',
                    !isSelected && !isCurrent && 'hover:bg-accent',
                    isCurrent && 'opacity-50 cursor-not-allowed'
                )}
                style={{ paddingLeft: `${depth * 16 + 8}px` }}
                onClick={handleSelect}
            >
                {hasChildren ? (
                    <button
                        onClick={handleToggle}
                        className="p-0.5 hover:bg-accent-foreground/10 rounded"
                    >
                        {expanded ? (
                            <ChevronDown className="w-4 h-4" />
                        ) : (
                            <ChevronRight className="w-4 h-4" />
                        )}
                    </button>
                ) : (
                    <span className="w-5" />
                )}

                {expanded ? (
                    <FolderOpen className="w-4 h-4 flex-shrink-0" />
                ) : (
                    <Folder className="w-4 h-4 flex-shrink-0" />
                )}

                <span className="text-sm truncate flex-1">{node.name}</span>

                {isCurrent && (
                    <span className="text-xs text-muted-foreground">(current)</span>
                )}
            </div>

            {expanded && hasChildren && (
                <div>
                    {node.children!.map((child) => (
                        <AlbumTreeItem
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                            selectedId={selectedId}
                            currentAlbumId={currentAlbumId}
                            onSelect={onSelect}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
